import { useStorage } from '@fold-dev/core'
import { API_HOST } from '../constants'
import { fetched } from '../helpers/util'

export const FRONTEER_JWT = 'FRONTEER_JWT'
export const FRONTEER_JWT_TEMP = 'FRONTEER_JWT_TEMP'

export function useAuth() {
    const { setStorage, getStorage, deleteStorage } = useStorage()

    const currentAuthenticatedUser = async () => {
        const token = getStorage(FRONTEER_JWT)
        if (!token) {
            throw new Error('Not authenticated')
        } else {
            const { exp, sub, userId } = parseJwt(token)
            const now = new Date().getTime() / 1000
            if (exp < now) {
                throw new Error('Not authenticated')
            } else {
                return { token, exp, sub, userId }
            }
        }
    }

    const parseJwt = (token): any => {
        try {
            if (!token) throw new Error('Not a valid token')
            const base64Url: string = token.split('.')[1]
            const base64: string = base64Url.replace('-', '+').replace('_', '/')
            return JSON.parse(window.atob(base64))
        } catch (e) {
            console.log(e)
        }
    }

    const saveToken = (token: string): void => {
        setStorage(FRONTEER_JWT, token)
    }

    const getUserEmail = () => {
        const token = getStorage(FRONTEER_JWT)
        if (!token) return null
        return parseJwt(token).email
    }

    const getUserId = () => {
        const token = getStorage(FRONTEER_JWT)
        if (!token) return null
        return parseJwt(token).sub
    }

    const getToken = () => {
        const token = getStorage(FRONTEER_JWT)
        if (!token) return ''
        const expiry = parseJwt(token).exp
        const epoch = Math.floor(new Date().getTime() / 1000)
        return expiry < epoch ? '' : token
    }

    const deleteToken = (): void => {
        deleteStorage(FRONTEER_JWT)
    }

    const updateMe = async (payload) => {
        const token = getStorage(FRONTEER_JWT)
        return await fetched({
            url: `${API_HOST}/users`,
            method: 'PUT',
            body: { ...payload },
            token,
        })
    }

    const getMe = async () => {
        const token = getStorage(FRONTEER_JWT)
        return await fetched({
            url: `${API_HOST}/users`,
            method: 'GET',
            token,
        })
    }

    const logout = () => {
        deleteToken()
        window.location.href = window.location.origin + '/auth'
    }

    const login = async ({ email, password }) => {
        const { token } = await fetched({
            url: `${API_HOST}/auth/login`,
            method: 'POST',
            body: { email, password },
        })
        if (!token) {
            throw Error('Error getting login token')
        } else {
            saveToken(token)
            return token
        }
    }

    const signup = async ({ email, name, password, code }) => {
        const token = await fetched({
            url: `${API_HOST}/auth/signup`,
            method: 'POST',
            body: { email, name, password, code },
        })

        saveToken(token)
        return token
    }

    const resetPassword = async ({ email }) => {
        return await fetched({
            url: `${API_HOST}/auth/reset-password`,
            method: 'GET',
            query: { email },
        })
    }

    // token here is an externally created JWT
    // not the normal ones used for auth
    const updateAccountPassword = async ({ newPassword, oldPassword }) => {
        const token = getStorage(FRONTEER_JWT)
        return await fetched({
            url: `${API_HOST}/auth/update-account-password`,
            method: 'PUT',
            body: { newPassword, oldPassword },
            token,
        })
    }

    // token here is an externally created JWT
    // not the normal ones used for auth
    const updatePassword = async ({ token, password }) => {
        return await fetched({
            url: `${API_HOST}/auth/update-password`,
            method: 'PUT',
            body: { password },
            token,
        })
    }

    const reIssueToken = async () => {
        const token = await fetched({
            url: `${API_HOST}/auth/re-issue-jwt`,
            method: 'POST',
        })
        saveToken(token)
        return token
    }

    return {
        currentAuthenticatedUser,
        saveToken,
        getUserId,
        getUserEmail,
        getToken,
        deleteToken,
        parseJwt,
        login,
        logout,
        resetPassword,
        updateAccountPassword,
        updatePassword,
        updateMe,
        getMe,
        signup,
        reIssueToken,
    }
}
